import { format } from "date-fns";
import {
  useCreate,
  useDelete,
  useGetIdentity,
  useGetList,
  useNotify,
  useUpdate,
} from "ra-core";
import { useState } from "react";

import { useDateLocale } from "../../personal/useDateLocale";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import type { Contact, ContactNote, Deal } from "../../types";
import { getSaleName, useActiveSales } from "../useActiveSales";
import { useRefreshBoard } from "../useRefreshBoard";
import { DealsSection, type NewDeal } from "./DealsSection";
import { DocumentsSection } from "./DocumentsSection";
import { NotesSection } from "./NotesSection";
import { toAttachment } from "./useCreateCard";

/**
 * Negotiations (deals), documents and notes of the card's contact. Documents
 * are note attachments: a document attached here is stored as a note with no
 * text, so it also shows on the contact page.
 */
export const CardActivity = ({ contact }: { contact: Contact }) => {
  const notify = useNotify();
  const refreshBoard = useRefreshBoard();
  const { identity } = useGetIdentity();
  const { dealStages } = useConfigurationContext();
  const { dateFnsLocale } = useDateLocale();
  const sales = useActiveSales();
  const [create] = useCreate();
  const [update] = useUpdate<ContactNote>();
  const [deleteOne] = useDelete();
  const [isUploading, setIsUploading] = useState(false);

  const { data: deals = [] } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "ASC" },
    filter: { "contact_ids@cs": `{${contact.id}}`, "archived_at@is": null },
  });
  const { data: notes = [] } = useGetList<ContactNote>("contact_notes", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "date", order: "DESC" },
    filter: { contact_id: contact.id },
  });

  const run = async (action: () => Promise<unknown>, message: string) => {
    try {
      await action();
      await refreshBoard();
      notify(message);
    } catch (error: unknown) {
      notify(
        error instanceof Error ? error.message : "ra.notification.http_error",
        { type: "error" },
      );
    }
  };

  const addDeal = (deal: NewDeal) =>
    run(
      () =>
        create(
          "deals",
          {
            data: {
              name: deal.name,
              amount: deal.amount ?? 0,
              description: deal.description || null,
              contact_ids: [contact.id],
              company_id: contact.company_id ?? null,
              stage: dealStages[0]?.value ?? "opportunity",
              sales_id: contact.sales_id ?? identity?.id,
              index: 0,
            },
          },
          { returnPromise: true },
        ),
      "crm.operations.deals.created",
    );

  const addNote = (text: string) =>
    run(
      () =>
        create(
          "contact_notes",
          {
            data: {
              contact_id: contact.id,
              text,
              date: new Date().toISOString(),
              sales_id: identity?.id,
            },
          },
          { returnPromise: true },
        ),
      "crm.operations.notes.created",
    );

  const attachFiles = async (files: File[]) => {
    setIsUploading(true);
    await run(
      () =>
        create(
          "contact_notes",
          {
            data: {
              contact_id: contact.id,
              text: "",
              date: new Date().toISOString(),
              sales_id: identity?.id,
              attachments: files.map(toAttachment),
            },
          },
          { returnPromise: true },
        ),
      "crm.operations.documents.added",
    );
    setIsUploading(false);
  };

  const deleteDocument = (key: string | number) => {
    const [noteId, index] = String(key).split(":");
    const note = notes.find((candidate) => String(candidate.id) === noteId);
    if (!note) return;
    const attachments = (note.attachments ?? []).filter(
      (_, position) => position !== Number(index),
    );
    return run(
      () =>
        attachments.length === 0 && !note.text?.trim()
          ? deleteOne(
              "contact_notes",
              { id: note.id, previousData: note },
              { returnPromise: true },
            )
          : update(
              "contact_notes",
              { id: note.id, data: { attachments }, previousData: note },
              { returnPromise: true },
            ),
      "crm.operations.documents.deleted",
    );
  };

  const formatNoteMeta = (note: ContactNote) =>
    [
      getSaleName(sales.find((sale) => sale.id === note.sales_id)),
      note.date
        ? format(new Date(note.date), "PPp", { locale: dateFnsLocale })
        : null,
    ]
      .filter(Boolean)
      .join(" · ");

  return (
    <>
      <DealsSection
        items={deals.map((deal) => ({
          key: deal.id,
          name: deal.name,
          amount: deal.amount,
          description: deal.description,
        }))}
        onAdd={addDeal}
        onDelete={(id) =>
          run(
            () =>
              deleteOne(
                "deals",
                { id, previousData: deals.find((deal) => deal.id === id) },
                { returnPromise: true },
              ),
            "crm.operations.deals.deleted",
          )
        }
      />
      <DocumentsSection
        items={notes.flatMap((note) =>
          (note.attachments ?? []).map((attachment, index) => ({
            key: `${note.id}:${index}`,
            title: attachment.title,
            src: attachment.src,
          })),
        )}
        isUploading={isUploading}
        onAttach={attachFiles}
        onDelete={deleteDocument}
      />
      <NotesSection
        items={notes
          .filter((note) => note.text?.trim())
          .map((note) => ({
            key: note.id,
            text: note.text,
            meta: formatNoteMeta(note),
          }))}
        onAdd={addNote}
        onDelete={(id) =>
          run(
            () =>
              deleteOne(
                "contact_notes",
                { id, previousData: notes.find((note) => note.id === id) },
                { returnPromise: true },
              ),
            "crm.operations.notes.deleted",
          )
        }
      />
    </>
  );
};
